/**
 * WhatsAppFab — le bouton de contact WhatsApp des pages publiques.
 *
 * Un lien `wa.me`, rien d'autre. Pas de script tiers, pas de widget
 * externe, pas de dépendance npm : l'icône est un SVG en ligne et le
 * clic ouvre l'application WhatsApp du visiteur. Rien n'est chargé
 * depuis un domaine tiers, donc rien à déclarer au bandeau de
 * consentement.
 *
 * CE QU'IL NE DIT PAS
 * -------------------
 * Aucune promesse de délai, aucun compteur de messages, aucun badge de
 * présence (« en ligne », « répond en 5 min »). Le bouton dit qu'on peut
 * écrire ; il ne dit pas quand on répondra. Pendant une période de
 * lancement, un « réponse immédiate » affiché à 23 h est une promesse
 * qu'on ne tient pas.
 *
 * PÉRIMÈTRE
 * ---------
 * Monté UNE fois, en frère du routeur (App.js), et non dans les pages :
 * il n'existe pas de layout public commun — chaque page publique importe
 * elle-même son `SiteHeaderV2` ou son `PublicHeader`. Monté à la racine,
 * il survit aux navigations sans se remonter, et aucune page n'est
 * touchée.
 *
 * Le filtre est `isPublicSiteRoute` (celui du bandeau cookies : une
 * seule définition de « qu'est-ce qu'une page publique »), moins
 * `/shared/*` — la vue d'un dossier partagé est lue par un tiers à qui
 * on a envoyé un lien, pas par un visiteur qui se demande ce qu'Archer
 * fait. `/attorney/apply` reste dehors pour la même raison : elle parle
 * à des avocats qui candidatent, et le message pré-rempli dit « une
 * question sur Archer ».
 *
 * Ce périmètre est plus étroit que celui d'Umami (`public/index.html`),
 * qui mesure aussi l'app connectée : un client dans son dossier n'a pas
 * besoin d'un bouton WhatsApp — il a le fil de son juriste.
 *
 * EMPILEMENT — pourquoi z-index 45
 * ---------------------------------
 * Le bas-droite des pages publiques est déjà habité. Relevé, tel qu'il
 * PEINT (et non tel que les feuilles de style l'annoncent) :
 *
 *   1400  CookieBanner .............. fixe, bas de page, pleine largeur
 *                                     sous ~752 px
 *   1300  siteV2/VideoModal ......... plein écran (HomeV2)
 *   1200  modale de paiement PlansV2  plein écran
 *    100  ToastViewport (ui/toast.jsx) conteneur permanent en bas à
 *                                     droite, mais `pointer-events:
 *                                     none` — il ne prend aucun clic,
 *                                     quel que soit l'étage du bouton
 *     50  l'en-tête public (`.public-header-v2`, `sticky`) ET, AVEC LUI,
 *         TOUT LE MENU MOBILE
 *  →  45  CE BOUTON
 *
 * La dernière ligne est celle qui commande la valeur, et elle n'est pas
 * lisible dans le CSS. `SiteMobileMenu` annonce z-index 190 (voile) et
 * 200 (panneau), mais il est rendu DANS le `<nav>` de l'en-tête, qui est
 * `position: sticky; z-index: 50` — donc un contexte d'empilement. Ses
 * 190 et 200 ne valent qu'À L'INTÉRIEUR de ce contexte : vus de la
 * racine, le voile et le panneau peignent à 50. Un bouton à 150 passait
 * donc DEVANT le voile du menu ouvert (mesuré : le voile arrivait 4e
 * sous le curseur, le bouton 1er). Sous 50, l'ordre est le bon.
 *
 * Le seul étage que 45 abandonne est celui des toasts — sans
 * conséquence : leur conteneur est en `pointer-events: none` (mesuré),
 * et aucune des pages publiques n'émet de toast.
 *
 * `FloatingChatButton` (z-40, `bottom-6 right-6`) occupe le même coin
 * mais vit dans `Layout` — l'app connectée, où ce bouton-ci ne se monte
 * jamais : les deux ne peuvent pas se croiser.
 *
 * Reste le bandeau cookies, qui partage exactement ce coin sur mobile.
 * Plutôt que de décaler le bouton d'une hauteur de bandeau — hauteur qui
 * dépend du retour à la ligne, donc de la langue —, le bouton NE SE
 * MONTE PAS tant que le consentement n'est pas tranché. C'est un
 * événement de première visite qui se referme en un clic, et
 * `CONSENT_EVENT` fait apparaître le bouton sans rechargement.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isPublicSiteRoute } from './CookieBanner';
import { CONSENT_EVENT, needsConsentDecision } from '../lib/consent';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
import { useWhatsAppContact } from '../hooks/usePublicConfig';
import useMediaQuery from '../hooks/useMediaQuery';
import { createT } from '../i18n/createT';
import dict from '../i18n/whatsapp.json';
import { track } from '../utils/analytics';

/** Le glyphe WhatsApp (simple-icons), tracé en ligne — aucune dépendance. */
const WHATSAPP_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.347-.347.52-.52.174-.174.232-.298.347-.497.116-.198.058-.371-.03-.52-.086-.148-.663-1.594-.909-2.183-.239-.574-.482-.497-.66-.505l-.56-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

/** Vert WhatsApp officiel. */
const WHATSAPP_GREEN = '#25D366';

/**
 * Routes retirées EN PLUS de ce que `isPublicSiteRoute` écarte déjà.
 *
 *  · `/shared/*` — la vue d'un dossier partagé, ouverte par un tiers à
 *    qui on a envoyé un lien. Elle est publique, mais ce n'est pas une
 *    surface d'acquisition : le bandeau cookies y a sa place, ce bouton
 *    non.
 *  · `/attorney/*` — le portail avocat historique, et `/attorney/apply`
 *    avec lui : la candidature parle à des avocats, quand le message
 *    pré-rempli dit « une question sur Archer ».
 */
const EXCLUDED = ['/shared', '/attorney'];

/**
 * Les écrans du portail avocat sous `/attorneys/*`.
 *
 * On ne peut pas trancher par préfixe : le segment suivant est soit un
 * écran du portail, soit le SLUG d'un profil public — et `/attorneys`
 * (Ton équipe) comme `/attorneys/<slug>` SONT des pages publiques. Cette
 * liste recopie donc les écrans déclarés dans App.js. UN ÉCRAN AJOUTÉ
 * LÀ-BAS DOIT ÊTRE AJOUTÉ ICI, comme dans `public/index.html` pour
 * Umami.
 *
 * Elle ne fait pas doublon avec `isPublicSiteRoute` par confort : ce
 * prédicat rend `true` sur ces chemins. Son exception
 * `PUBLIC_ATTORNEY_EXCEPTIONS` écarte bien les écrans du portail, mais
 * le repli qui suit teste le préfixe `/attorney` avec un slash, lequel
 * n'attrape pas `/attorneys/login` — le bandeau cookies s'affiche donc
 * sur le portail avocat. C'est un défaut ANTÉRIEUR à ce composant, il
 * est signalé, et il n'est pas corrigé ici : ce chantier ne touche pas
 * au bandeau.
 */
const PORTAIL_AVOCAT =
  /^\/attorneys\/(join|login|dashboard|desk|inbox|my-cases|completed|cases|earnings|live-counsel|profile|onboarding)(\/|$)/;

function isWhatsAppRoute(pathname) {
  const path = String(pathname || '/');
  if (EXCLUDED.some((p) => path === p || path.startsWith(`${p}/`))) return false;
  if (PORTAIL_AVOCAT.test(path)) return false;
  return isPublicSiteRoute(path);
}

export default function WhatsAppFab() {
  const { pathname } = useLocation();
  const [lang] = useSiteLanguage();
  const t = useMemo(() => createT(dict, lang), [lang]);
  const { show_whatsapp_contact: enabled, whatsapp_contact_number: number } =
    useWhatsAppContact();

  // Périphérique qui sait survoler : l'étiquette n'a de sens que là. Sur
  // un écran tactile, `mouseenter` part au premier contact du doigt et
  // l'étiquette clignoterait pendant que WhatsApp s'ouvre.
  const canHover = useMediaQuery('(hover: hover)');
  const isCompact = useMediaQuery('(max-width: 640px)');
  const [hovered, setHovered] = useState(false);

  // Consentement : caché tant qu'il n'est pas tranché (cf. l'en-tête).
  // On part de `true` — donc rien à l'écran — et on demande à l'effet :
  // afficher le bouton puis le retirer aurait fait un clignotement à
  // chaque première visite.
  const [consentPending, setConsentPending] = useState(true);
  useEffect(() => {
    // En pré-rendu, le bouton reste absent (cf. CookieBanner, même raison).
    if (typeof window !== 'undefined' && window.__ARCHER_PRERENDER__) return undefined;
    const read = () => setConsentPending(needsConsentDecision());
    read();
    window.addEventListener(CONSENT_EVENT, read);
    return () => window.removeEventListener(CONSENT_EVENT, read);
  }, []);

  // Le flag ET le numéro : l'un sans l'autre n'affiche rien.
  if (!enabled || !number) return null;
  if (consentPending) return null;
  if (!isWhatsAppRoute(pathname)) return null;

  const size = isCompact ? 52 : 56;
  const href = `https://wa.me/${number}?text=${encodeURIComponent(t('prefill'))}`;

  return (
    <div
      style={{
        position: 'fixed',
        right: 20,
        // Au-dessus de la barre de gestes iOS, comme le bandeau.
        bottom: 'max(20px, calc(env(safe-area-inset-bottom) + 20px))',
        // Sous l'en-tête public, donc sous le menu mobile qu'il contient.
        // Cf. le tableau d'empilement en tête de fichier.
        zIndex: 45,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
      data-testid="whatsapp-fab"
    >
      {canHover && hovered && (
        <span
          // Décoratif : le texte utile pour un lecteur d'écran est
          // l'`aria-label` du lien, qui dit où le clic emmène.
          aria-hidden="true"
          data-testid="whatsapp-fab-label"
          style={{
            whiteSpace: 'nowrap',
            background: '#ffffff',
            color: '#111827',
            border: '1px solid #ebebeb',
            borderRadius: 10,
            padding: '8px 12px',
            fontSize: 13.5,
            fontWeight: 600,
            lineHeight: 1.2,
            boxShadow: '0 12px 30px -12px rgba(15, 23, 42, .35)',
          }}
        >
          {t('label')}
        </span>
      )}

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('aria')}
        data-testid="whatsapp-fab-link"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => track('whatsapp_clicked', { page: pathname })}
        style={{
          // 52 px au plus petit : au-delà de la cible tactile de 44 px
          // recommandée, bords compris.
          width: size,
          height: size,
          borderRadius: '50%',
          background: WHATSAPP_GREEN,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 28px -8px rgba(15, 23, 42, .38)',
          textDecoration: 'none',
          transition: 'transform .15s ease, box-shadow .15s ease',
          transform: canHover && hovered ? 'scale(1.05)' : 'none',
        }}
      >
        <svg
          width={size === 56 ? 30 : 28}
          height={size === 56 ? 30 : 28}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <path d={WHATSAPP_PATH} />
        </svg>
      </a>
    </div>
  );
}
